/**
 * AP-36 Phase D — **die Speicherstelle der Projekt-Einstellungen** (`projectSettings`).
 *
 * `ProjectSetting` ist eine Zeile je Mandant, geschrieben aber **je Reiter**
 * (`PATCH /projects/settings/:tab`): Der Server nimmt an einem Reiter nur dessen
 * Felder an, weil der Lesefilter nicht gebuchter Bausteine sonst beim
 * Zurückschreiben verloren ginge (AP-29-N3). `schreibe` verteilt den Patch
 * deshalb nach der Zuordnung unten und reicht den Stand von Aufruf zu Aufruf
 * weiter.
 *
 * Die Zuordnung ist dieselbe wie `SETTING_TABS` im Backend
 * (`project-settings.service.ts`). Ein Feld, das hier fehlt, antwortet mit
 * `SETTING_FIELD_WRONG_TAB` — das fällt beim ersten Speichern auf.
 */
import { api } from '@/lib/axios'
import { mitStand, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'

export const REITER_FELDER: Record<string, readonly string[]> = {
  allgemein: [
    'defaultStatus',
    'requireSite',
    'requireClientOnOrder',
    'warrantyMonths',
    'defaultUnitId',
    'idleDays',
    'defaultSalesUserId',
    'defaultCalculationUserId',
    'maxFileMb',
    'workOrderPaper',
    'workOrderSignatures',
    'workStepQuantityBasisDefault',
  ],
  nummernkreis: ['numberPattern', 'numberPrefix', 'numberDigits', 'yearlyReset', 'allowManualNumber', 'nextWorkStepNumber'],
  zeiterfassung: ['mirrorAll', 'mirrorStatuses', 'actualsCheckedOnly', 'defaultPlannedCostRate'],
  abrechnung: ['defaultBillingScheme', 'defaultBillingInterval', 'billingWeekday', 'regieGrouping', 'regieNamesOnInvoice', 'invoiceRequiresDelivery'],
  stueckliste: ['procurementApproval', 'bomInquiryApprovalAuto', 'bomOrderApprovalAuto', 'bomQuantityBasisDefault'],
  technik: ['manufacturer', 'declarationNumberPattern'],
  ampeln: ['scheduleWarnDays', 'costWarnFactor', 'costAlertPercent', 'progressWarnPoints', 'progressAlertPoints'],
  cockpits: ['cockpitHideDoneAfterDays', 'assignmentDefaultDays', 'dayHoursWarn', 'weekHoursWarn'],
  erinnerungen: ['taskRemindOffsetHours', 'remindDailyDigest', 'digestTime'],
  pwa: ['folderBundleMaxPositions'],
}

const REITER_JE_FELD: Record<string, string> = Object.fromEntries(
  Object.entries(REITER_FELDER).flatMap(([reiter, felder]) => felder.map((f) => [f, reiter])),
)

/** Den Patch je Reiter senden; der Stand der einen Antwort gilt für den nächsten Aufruf. */
async function schreibe(patch: Datensatz, stand: string | undefined): Promise<Datensatz> {
  const jeReiter = new Map<string, Datensatz>()
  for (const [feld, wert] of Object.entries(patch)) {
    const reiter = REITER_JE_FELD[feld] ?? 'allgemein'
    jeReiter.set(reiter, { ...(jeReiter.get(reiter) ?? {}), [feld]: wert })
  }
  let antwort: Datensatz | undefined
  let standJetzt = stand
  for (const [reiter, teil] of jeReiter) {
    antwort = (await api.patch<Datensatz>(`/projects/settings/${reiter}`, teil, mitStand(standJetzt))).data
    standJetzt = ((antwort.data as Datensatz | undefined)?.updatedAt as string | undefined) ?? standJetzt
  }
  return antwort ?? (await api.get<Datensatz>('/projects/settings')).data
}

export function useProjektEinstellungen(daten: Datensatz | null): Speicherstelle<unknown> {
  return useSpeicherstelle<Datensatz>({
    schluessel: 'projectSettings',
    bezeichnung: 'Einstellungen des Projektmanagements',
    daten,
    schreibe,
  }) as Speicherstelle<unknown>
}

/** Stelle einer Listenzeile der Einstellungen (Phase, Qualifikation, Journal-Typ, Stücklisten-Kategorie). */
export function useZeilenStelle(art: string, url: string, zeile: { id: string }, bezeichnung: string): Speicherstelle<unknown> {
  return useSpeicherstelle<Datensatz>({
    schluessel: `${art}:${zeile.id}`,
    bezeichnung,
    daten: zeile as unknown as Datensatz,
    schreibe: (patch, stand) => api.patch<Datensatz>(url, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url, method: 'PATCH' },
  }) as Speicherstelle<unknown>
}
